<template>
  <CSidebar
    class="aw-overlay-sidebar aw-overlay-sidebar-top"
    aside
    :show="isVisible"
    @update:show="(val) => !val && closeQuickEdit()"
    colorScheme="light"
    overlaid
    size="xl"
  >
    <div class="scrollable">
      <!-- Header -->
      <div class="d-flex align-items-start p-3">
        <!-- Product Image -->
        <div v-if="product && product.image_url" class="mr-3">
          <img 
            :src="product.image_url" 
            :alt="product.name"
            class="product-image-header"
          />
        </div>
        <div v-else class="mr-3">
          <div class="product-image-placeholder">
            <CIcon name="cil-image" size="2xl" class="text-muted"/>
          </div>
        </div>

        <div v-if="product" class="flex-grow-1">
          <h5>{{ product.name }}</h5>
          <div>
            <CButton
              size="sm"
              color="link"
              class="p-0"
              @click="handleDetailedEdit"
            >
              <CIcon name="cil-arrow-right" /> {{ $t('product.detailed_edit') }}
            </CButton>
          </div>
        </div>

        <div style="height: 56px">
          <CSidebarClose @click.native="closeQuickEdit" />
        </div>
      </div>

      <CTabs
        v-if="product"
        class="quick-edit-tabs"
        variant="tabs"
        :active-tab.sync="activeQuickEditTab"
      >
        <CTab>
          <template slot="title">
            <CIcon name="cil-pencil" />
            <span>{{ $t('product.edit_product') }}</span>
          </template>

          <CListGroup class="list-group-accent quick-edit-tab-content">
            <!-- Product Form -->
            <CListGroupItem class="list-group-item-accent-primary">
              <CForm @submit.prevent="updateProduct">
                <div class="mb-3">
                  <CInput
                    :label="$t('product.gtin')"
                    :placeholder="$t('product.gtin')"
                    :value.sync="$v.product.gtin.$model"
                  />
                </div>

                <div class="mb-3">
                  <CInput
                    :label="$t('product.product_name')"
                    :placeholder="$t('product.product_name')"
                    :value.sync="$v.product.name.$model"
                    :isValid="checkIfValid('name')"
                    :invalidFeedback="$t('validation.required_min_length', { length: '2' })"
                  />
                </div>

                <div class="mb-3">
                  <CInput
                    :label="$t('product.product_name_short')"
                    :placeholder="$t('product.product_name_short')"
                    :value.sync="$v.product.name_short.$model"
                  />
                </div>

                <div class="mb-3">
                  <label class="d-block" style="font-weight: 500">{{ $t('product.description') }}</label>
                  <quill-editor
                    style="min-height: 160px;"
                    ref="quill_editor_d"
                    v-model="$v.product.description_short.$model"
                    :options="{ ...editorOption, placeholder: $t('product.description') }"
                  />
                </div>

                <div v-if="isRestaurant" class="mb-3 d-flex flex-column flex-md-row flex-gap-3">
                  <div class="d-flex align-items-center">
                    <strong class="mr-2">{{ $t('product.orderable') }}</strong>
                    <CSwitch
                      :checked.sync="$v.product.orderable.$model"
                      :lazy="false"
                      :labelOn="$t('global.switch_yes')"
                      :labelOff="$t('global.switch_no')"
                      color="success"
                      shape="pill"
                    />
                  </div>

                  <div class="d-flex align-items-center">
                    <strong class="mr-2">{{ $t('product.cook_station') }}</strong>
                    <CSwitch
                      :checked.sync="$v.product.cook_station.$model"
                      :lazy="false"
                      :labelOn="$t('global.switch_yes')"
                      :labelOff="$t('global.switch_no')"
                      color="success"
                      shape="pill"
                    />
                  </div>
                </div>
              </CForm>
            </CListGroupItem>
          </CListGroup>
        </CTab>

        <CTab v-if="isRestaurant">
          <template slot="title">
            <CIcon name="cil-link" />
            <span>{{ $t('product.equivalent_products') }}</span>
          </template>

          <CListGroup class="list-group-accent quick-edit-tab-content">
            <CListGroupItem class="list-group-item-accent-info">
              <product-equivalents
                :productId="product.id"
                :firmId="selectedFirm.id"
                compact
              />
            </CListGroupItem>
          </CListGroup>
        </CTab>
      </CTabs>

      <mc-spinner :opacity="0.8" v-show="loading" center />
    </div>

    <!-- Sticky Update Button -->
    <div class="sticky-bottom-button" v-if="product && activeQuickEditTab === 0">
      <CButton
        color="primary"
        :disabled="loading"
        @click="updateProduct"
        class="w-100"
      >
        <CIcon name="cil-send"/> <span class="align-middle">{{ $t('global.submit') }}</span>
      </CButton>
    </div>
  </CSidebar>
</template>

<script>
import { validationMixin } from "vuelidate"
import { required, minLength } from "vuelidate/lib/validators"

// *** Text Editors
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { quillEditor } from 'vue-quill-editor'
import ProductEquivalents from "@/components/ProductEquivalents";

export default {
  name: "ProductQuickEdit",
  mixins: [validationMixin],
  components: {
    quillEditor,
    ProductEquivalents,
  },
  props: {
    productId: {
      type: [Number, String, null],
      required: false,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      product: null,
      activeQuickEditTab: 0,

      // quill-editor
      editorOption: {
        theme: 'bubble',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
          ]
        }
      },
    };
  },

  watch: {
    reloadParams() {
      this.product = null;
      this.activeQuickEditTab = this.getQuickEditTabIndex(this.requestedQuickEditTab);
      this.loadProduct();
    },

    requestedQuickEditTab(tab) {
      if (this.isVisible) {
        this.activeQuickEditTab = this.getQuickEditTabIndex(tab);
      }
    },

    // isVisible: { handler: "loadProduct" },
    // productId: { handler: "loadProduct" },
  },

  computed: {
    reloadParams() { return [this.productId, this.isVisible]; },
    isVisible() { return this.$store.state.asideProductQuickEditId != null && this.productId == this.$store.state.asideProductQuickEditId; },
    requestedQuickEditTab() { return this.$store.state.asideProductQuickEditTab || 'edit'; },
  },

  methods: {
    getQuickEditTabIndex(tab) {
      if (tab === 'equivalents' && this.isRestaurant) {
        return 1;
      }

      return 0;
    },

    closeQuickEdit() {
      this.$store.commit('set', ['asideProductQuickEditId', null]);
      this.$store.commit('set', ['asideProductQuickEditTab', 'edit']);
    },

    loadProduct() {
      if (this.isVisible !== true || !this.productId) {
        return;
      }

      this.loading = true;
      
      this.$axios.get(this.$backend.get('PRODUCT', 'QUICK_EDIT', this.productId))
        .then((response) => {
          // Get orderable from pivot if available (for menu products)
          const orderable = response.data.pivot && response.data.pivot.orderable !== undefined 
            ? response.data.pivot.orderable 
            : true;
          
          this.product = {
            id: response.data.id,
            gtin: response.data.gtin || '',
            name: response.data.name || '',
            name_short: response.data.name_short || '',
            description_short: response.data.description_short || '',
            active: response.data.active ?? true,
            cook_station: response.data.cook_station ?? false,
            orderable: orderable,
            image_url: response.data.image_url || response.data.cover_image_url || null,
          };
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load product');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    checkIfValid(fieldName) {
      // const field = this.$v.form[fieldName]
      const fields = fieldName.split(".");
      const field = fields.length > 1
        ? this.$v.product[fields[0]][fields[1]]
        : this.$v.product[fieldName];

      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "");
    },

    updateProduct() {
      // Validation
      this.$v.product.$touch();
      if (this.$v.product.$invalid) {
        return;
      }

      this.loading = true;
      
      const payload = {
        gtin: this.product.gtin,
        name: this.product.name,
        name_short: this.product.name_short,
        description_short: this.product.description_short,
        active: this.product.active,
        cook_station: this.product.cook_station,
      };
      
      // Add orderable if restaurant product
      if (this.isRestaurant && this.product.orderable !== undefined) {
        payload.orderable = this.product.orderable;
      }
      
      this.$axios.put(this.$backend.get('PRODUCT', 'QUICK_EDIT', this.product.id), payload)
        .then((response) => {          
          // Update the product data with response data
          const updatedProduct = response.data.product;
          Object.assign(this.product, updatedProduct);

          this.$toast.success(updatedProduct.name + ' ' + this.$t('global.updated'));
          
          // Prepare event data with pivot information if orderable was updated
          const eventData = {
            ...updatedProduct,
            orderable: this.product.orderable, // Include orderable status
            pivot: updatedProduct.pivot || (this.product.orderable !== undefined ? {
              orderable: this.product.orderable
            } : null)
          };
          
          // Emit event to parent to refresh data
          this.$emit('product-quick-updated', eventData);

          // Close the sidebar
          this.closeQuickEdit();
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || error.response?.data?.error || 'Update failed');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleDetailedEdit() {
      const isProductEditModalOpen = this.$store.state.productEditModalId !== null && this.$store.state.productEditModalId !== undefined;
      this.closeQuickEdit();

      if (isProductEditModalOpen) {
        this.$root.$emit('product-quick-detailed-edit', this.product.id);
        return;
      }

      this.$store.commit('set', ['productEditModalId', this.product.id]);
    }
  },

  validations: {
    product: {
      gtin: {},
      name: { required, minLength: minLength(2) },
      name_short: {},
      description_short: {},
      cook_station: {},
      orderable: {},
    }
  }
};
</script>

<style scoped>
.scrollable {
  overflow-y: auto;
  height: calc(100% - 70px); /* Sticky button için yer bırak */
  padding-bottom: 1rem;
}

.product-image-header {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.product-image-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
}

.sticky-bottom-button {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(transparent, rgba(255, 255, 255, 0.9) 20%, white 50%);
  backdrop-filter: blur(4px);
  border-top: 1px solid #e9ecef;
}

.quick-edit-tabs {
  border-top: 1px solid #e9ecef;
}

.quick-edit-tabs ::v-deep .nav-tabs {
  background: #fff;
  border-bottom: 1px solid #d8dbe0;
}

.quick-edit-tabs ::v-deep .nav-item {
  flex: 1 1 0;
  text-align: center;
}

.quick-edit-tabs ::v-deep .nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  min-height: 44px;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #536c8f;
  font-weight: 600;
}

.quick-edit-tabs ::v-deep .nav-link.active {
  color: #321fdb;
  background: transparent;
  border-bottom-color: #321fdb;
}

.quick-edit-tabs ::v-deep .tab-content,
.quick-edit-tabs ::v-deep .tab-pane {
  background: transparent;
}

.quick-edit-tab-content {
  padding-top: 0.75rem;
}
</style>
